/**
 * A symbol shape drawn from its data (the symbols change of 2026-10-31): the SymbolShape and LineStyle of
 * `symbols/symbol-library.schema.yaml` turned into SVG elements, the way the Design Studio's `render.py` does it.
 * Local millimetres, reference point at (0, 0), Y axis down, angles clockwise on the screen: an SVG viewBox in
 * millimetres needs no flip. Nothing here reads a picture file; the shape is the picture.
 */

export interface ShapePoint {
  x_mm: number;
  y_mm: number;
}

export interface ConnectionPoint extends ShapePoint {
  name: string;
  direction: string;
  angle_deg?: number;
  domain?: string;
  flow?: string;
  role_hint?: string;
  unsure: boolean;
}

/** One SVG element: its tag, its attributes, and the text of a `text` element. */
export interface SvgPart {
  tag: "path" | "circle" | "ellipse" | "text";
  attrs: Record<string, string>;
  text?: string;
}

export interface Frame {
  x: number;
  y: number;
  width: number;
  height: number;
}

type Data = Record<string, unknown>;

/** The fixed dash table of the schema (ShapeDash): lengths as multiples of the stroke width. */
const DASH_X_WIDTH: Record<string, number[]> = {
  dash: [4, 2],
  short_dash: [2, 2],
  dot: [1, 2],
  long_dash: [8, 2],
  dash_short_dash: [4, 2, 2, 2],
  long_dash_short_dash: [8, 2, 2, 2],
  long_dash_short_dash_short_dash: [8, 2, 2, 2, 2, 2],
};

function isData(value: unknown): value is Data {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** A number of the shape (the server sends floats; a text that reads as a number is taken too). */
export function num(value: unknown, fallback = 0): number {
  const n = typeof value === "number" ? value : typeof value === "string" ? Number(value) : NaN;
  return Number.isFinite(n) ? n : fallback;
}

/** A number as SVG text: at most four decimals, no trailing zeros. */
export function fmt(value: number): string {
  const rounded = Math.round(value * 10000) / 10000;
  return Object.is(rounded, -0) ? "0" : String(rounded);
}

function point(value: unknown): ShapePoint {
  const p = isData(value) ? value : {};
  return { x_mm: num(p.x_mm), y_mm: num(p.y_mm) };
}

function P(value: unknown): string {
  const p = point(value);
  return `${fmt(p.x_mm)} ${fmt(p.y_mm)}`;
}

function list(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

export function dashArray(stroke: Data): number[] | null {
  const exact = list(stroke.dash_array_mm).map((v) => num(v));
  if (exact.length >= 2) return exact;
  const named = DASH_X_WIDTH[String(stroke.dash ?? "solid")];
  return named ? named.map((m) => m * num(stroke.width_mm)) : null;
}

function style(p: Data): Record<string, string> {
  const out: Record<string, string> = {};
  const stroke = isData(p.stroke) ? p.stroke : null;
  if (stroke && num(stroke.width_mm) > 0) {
    out.stroke = typeof stroke.colour === "string" ? stroke.colour : "currentColor";
    out["stroke-width"] = fmt(num(stroke.width_mm));
    out["stroke-linecap"] = String(stroke.cap ?? "round");
    out["stroke-linejoin"] = String(stroke.join ?? "round");
    const dashes = dashArray(stroke);
    if (dashes) out["stroke-dasharray"] = dashes.map(fmt).join(" ");
  } else {
    out.stroke = "none";
  }
  const fill = isData(p.fill) ? p.fill : null;
  if (fill && fill.kind === "solid") {
    out.fill = typeof fill.colour === "string" ? fill.colour : "currentColor";
    if (fill.rule === "evenodd") out["fill-rule"] = "evenodd";
  } else {
    out.fill = "none";
  }
  return out;
}

function arcPoint(p: Data, deg: number): ShapePoint {
  const c = point(p.centre);
  const rx = num(p.radius_x_mm);
  const ry = num(p.radius_y_mm);
  const rot = (num(p.rotation_deg) * Math.PI) / 180;
  const t = (deg * Math.PI) / 180;
  return {
    x_mm: c.x_mm + Math.cos(rot) * rx * Math.cos(t) - Math.sin(rot) * ry * Math.sin(t),
    y_mm: c.y_mm + Math.sin(rot) * rx * Math.cos(t) + Math.cos(rot) * ry * Math.sin(t),
  };
}

function arcCommand(p: Data): { start: ShapePoint; command: string } {
  const start = num(p.start_deg);
  const sweep = num(p.sweep_deg);
  const from = arcPoint(p, start);
  if (Math.abs(sweep) >= 360) {
    // a whole turn cannot be one SVG arc: two halves
    const half = arcPoint(p, start + sweep / 2);
    const flag = sweep > 0 ? 1 : 0;
    const r = `${fmt(num(p.radius_x_mm))} ${fmt(num(p.radius_y_mm))} ${fmt(num(p.rotation_deg))}`;
    return { start: from, command: `A ${r} 0 ${flag} ${fmt(half.x_mm)} ${fmt(half.y_mm)} A ${r} 0 ${flag} ${fmt(from.x_mm)} ${fmt(from.y_mm)}` };
  }
  const to = arcPoint(p, start + sweep);
  const large = Math.abs(sweep) > 180 ? 1 : 0;
  const flag = sweep > 0 ? 1 : 0;
  return {
    start: from,
    command: `A ${fmt(num(p.radius_x_mm))} ${fmt(num(p.radius_y_mm))} ${fmt(num(p.rotation_deg))} ${large} ${flag} ${fmt(to.x_mm)} ${fmt(to.y_mm)}`,
  };
}

/** The path data of one piece of a curve; the first piece moves to its start, the others draw a line to it. */
function partPath(p: Data, first: boolean): string {
  const kind = p.kind;
  const lead = (at: unknown) => (first ? `M ${P(at)} ` : `L ${P(at)} `);
  if (kind === "ShapePolyline") {
    const pts = list(p.points);
    if (pts.length === 0) return "";
    return lead(pts[0]) + pts.slice(1).map((q) => `L ${P(q)}`).join(" ");
  }
  if (kind === "ShapeLine") return lead(p.from_point) + `L ${P(p.to_point)}`;
  if (kind === "ShapeBSpline") {
    const c = list(p.control_points);
    if (c.length === 0) return "";
    const degree = num(p.degree, 3);
    const head = first ? `M ${P(c[0])} ` : `L ${P(c[0])} `;
    if (degree === 3 && c.length === 4) return head + `C ${c.slice(1).map(P).join(" ")}`;
    if (degree === 2 && c.length === 3) return head + `Q ${c.slice(1).map(P).join(" ")}`;
    return head + c.slice(1).map((q) => `L ${P(q)}`).join(" "); // a general spline: its control polygon
  }
  if (kind === "ShapeArc") {
    const { start, command } = arcCommand(p);
    return lead(start) + command;
  }
  return "";
}

function boundaryPath(b: unknown): string {
  if (!isData(b)) return "";
  if (b.kind === "ShapePolyline") return `${partPath(b, true)} Z`;
  return `${list(b.parts).filter(isData).map((q, i) => partPath(q, i === 0)).join(" ")} Z`;
}

/** One primitive as an SVG element, or null for a kind that is not known. */
export function primitiveToSvg(value: unknown): SvgPart | null {
  if (!isData(value)) return null;
  const p = value;
  const look = style(p);
  switch (p.kind) {
    case "ShapeLine":
      return { tag: "path", attrs: { d: `M ${P(p.from_point)} L ${P(p.to_point)}`, ...look } };
    case "ShapePolyline":
      return { tag: "path", attrs: { d: partPath(p, true) + (p.closed ? " Z" : ""), ...look } };
    case "ShapeCircle": {
      const c = point(p.centre);
      return { tag: "circle", attrs: { cx: fmt(c.x_mm), cy: fmt(c.y_mm), r: fmt(num(p.radius_mm)), ...look } };
    }
    case "ShapeEllipse": {
      const c = point(p.centre);
      const attrs: Record<string, string> = { cx: fmt(c.x_mm), cy: fmt(c.y_mm), rx: fmt(num(p.radius_x_mm)), ry: fmt(num(p.radius_y_mm)), ...look };
      if (num(p.rotation_deg)) attrs.transform = `rotate(${fmt(num(p.rotation_deg))} ${fmt(c.x_mm)} ${fmt(c.y_mm)})`;
      return { tag: "ellipse", attrs };
    }
    case "ShapeArc":
    case "ShapeBSpline":
      return { tag: "path", attrs: { d: partPath(p, true), ...look } };
    case "ShapeCompositeCurve": {
      const d = list(p.parts).filter(isData).map((q, i) => partPath(q, i === 0)).join(" ");
      return { tag: "path", attrs: { d: d + (p.closed ? " Z" : ""), ...look } };
    }
    case "ShapeRegion": {
      const d = [boundaryPath(p.boundary), ...list(p.holes).map(boundaryPath)].join(" ");
      return { tag: "path", attrs: { d, ...look } };
    }
    case "ShapeText": {
      const [horizontal, vertical] = String(p.justification ?? "left_bottom").split("_");
      const height = num(p.height_mm, 1);
      const at = point(p.at);
      const y = at.y_mm + (vertical === "center" ? 0.35 * height : vertical === "top" ? 0.8 * height : 0);
      const attrs: Record<string, string> = {
        x: fmt(at.x_mm),
        y: fmt(y),
        "font-size": fmt(height),
        "font-family": `${String(p.font ?? "Arial")}, Helvetica, sans-serif`,
        "text-anchor": horizontal === "center" ? "middle" : horizontal === "right" ? "end" : "start",
        fill: typeof p.colour === "string" ? p.colour : "currentColor",
        stroke: "none",
      };
      if (num(p.rotation_deg)) attrs.transform = `rotate(${fmt(num(p.rotation_deg))} ${fmt(at.x_mm)} ${fmt(at.y_mm)})`;
      return { tag: "text", attrs, text: String(p.text ?? "") };
    }
    default:
      return null;
  }
}

export function primitivesOf(geometry: unknown): SvgPart[] {
  const g = isData(geometry) ? geometry : {};
  return list(g.primitives)
    .map(primitiveToSvg)
    .filter((part): part is SvgPart => part !== null);
}

function maxStroke(primitives: unknown[]): number {
  let width = 0;
  for (const p of primitives) {
    if (!isData(p)) continue;
    if (isData(p.stroke)) width = Math.max(width, num(p.stroke.width_mm));
  }
  return width;
}

/** The frame of a geometry (a symbol, or the sample of a line style): its extent, with half the widest stroke and a
 * little room around it, so that nothing touches the edge. */
export function frameOf(geometry: unknown, room = 0.08): Frame {
  const g = isData(geometry) ? geometry : {};
  const e = isData(g.extent) ? g.extent : {};
  const half = maxStroke(list(g.primitives)) / 2;
  const centreX = num(e.x_mm) + num(e.width_mm) / 2;
  const centreY = num(e.y_mm) + num(e.height_mm) / 2;
  const width = Math.max(num(e.width_mm), 0.5);
  const height = Math.max(num(e.height_mm), 0.5);
  const pad = Math.max(width, height) * room + half;
  return { x: centreX - width / 2 - pad, y: centreY - height / 2 - pad, width: width + 2 * pad, height: height + 2 * pad };
}

/** The connection points of a symbol, in the order of the shape. */
export function connectionPoints(shape: unknown): ConnectionPoint[] {
  const s = isData(shape) ? shape : {};
  const points = isData(s.connection_points) ? s.connection_points : {};
  return Object.entries(points).map(([name, value]) => {
    const p = isData(value) ? value : {};
    return {
      name,
      x_mm: num(p.x_mm),
      y_mm: num(p.y_mm),
      direction: String(p.direction ?? "east"),
      angle_deg: p.angle_deg === undefined || p.angle_deg === null ? undefined : num(p.angle_deg),
      domain: typeof p.domain === "string" ? p.domain : undefined,
      flow: typeof p.flow === "string" ? p.flow : undefined,
      role_hint: typeof p.role_hint === "string" ? p.role_hint : undefined,
      unsure: p.unsure === "yes",
    };
  });
}

/** The angle a line leaves a point at: its exact angle, or its side (0 east, 90 south, 180 west, 270 north). */
export function leavingAngle(p: ConnectionPoint): number {
  if (p.angle_deg !== undefined) return p.angle_deg;
  return { east: 0, south: 90, west: 180, north: 270 }[p.direction] ?? 0;
}

/** A line style drawn along y = 0 from x = 0 to `length` mm: its stroke, and its motif at each pitch. */
export function lineStyleParts(style: unknown, length: number): SvgPart[] {
  const s = isData(style) ? style : {};
  const out: SvgPart[] = [];
  const stroke = isData(s.stroke) ? s.stroke : null;
  if (stroke && num(stroke.width_mm) > 0) {
    const line = primitiveToSvg({ kind: "ShapeLine", from_point: { x_mm: 0, y_mm: 0 }, to_point: { x_mm: length, y_mm: 0 }, stroke });
    if (line) out.push(line);
  }
  const motif = isData(s.motif) ? s.motif : null;
  const pitch = motif ? num(motif.pitch_mm) : 0;
  if (motif && pitch > 0) {
    for (let x = num(motif.offset_mm); x < length; x += pitch) {
      for (const part of primitivesOf(motif)) {
        out.push({ ...part, attrs: { ...part.attrs, transform: `translate(${fmt(x)} 0)${part.attrs.transform ? ` ${part.attrs.transform}` : ""}` } });
      }
    }
  }
  return out;
}

/**
 * The parts with every stroke at least `minWidth` (in the units of the drawing, millimetres). A shape keeps its true
 * widths in its data; a small drawing on the screen (a tile, the item page, the live map) would turn them into
 * hairlines, so it draws each stroke at least about one pixel wide.
 */
export function withMinStroke(parts: SvgPart[], minWidth: number): SvgPart[] {
  if (!(minWidth > 0)) return parts;
  return parts.map((p) => {
    const width = Number(p.attrs["stroke-width"]);
    if (p.attrs.stroke === "none" || !Number.isFinite(width) || width >= minWidth) return p;
    return { ...p, attrs: { ...p.attrs, "stroke-width": fmt(minWidth) } };
  });
}
